import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowRight, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import "@/pa/pa-theme.css";

const STEPS = ["Name", "Voice & style", "Confirm"];

export default function PAOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    assistant_name: "", voice: "Emma (Female, British)", accent: "English",
    personality: "Luxury", plan: params.get("plan") || "pa_essential",
  });

  useEffect(() => {
    api.get("/pa/profile").then(({ data }) => {
      setMeta(data);
      if (data.profile?.onboarded) navigate("/pa");
      setForm((f) => ({ ...f, voice: data.voices?.[0] || f.voice }));
    }).catch(() => {});
  }, [navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ownerName = (user?.name || "").split(" ")[0] || "you";

  const submit = async () => {
    if (!form.assistant_name.trim()) { setStep(0); return toast.error("Please give your assistant a name"); }
    setBusy(true);
    try {
      await api.post("/pa/onboarding", { ...form, personality: form.personality.toLowerCase() });
      toast.success(`${form.assistant_name} is ready for you`);
      navigate("/pa");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set up your assistant");
    } finally { setBusy(false); }
  };

  if (!meta) return <div className="theme-pa min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin pa-gold" /></div>;

  return (
    <div className="theme-pa">
      <div className="pa-vignette min-h-screen flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          <div className="flex items-center gap-2 justify-center mb-8">
            <Sparkles className="h-6 w-6 pa-gold" strokeWidth={1.5} />
            <span className="pa-display text-2xl font-semibold">Design your assistant</span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`h-1.5 w-10 rounded-full transition-all duration-300`} style={{ background: i <= step ? "var(--pa-gold)" : "var(--pa-glass-border)" }} />
              </div>
            ))}
          </div>

          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="pa-glass p-8">
            {step === 0 && (
              <div>
                <h2 className="pa-display text-3xl">What should we call your assistant?</h2>
                <p className="pa-muted text-sm mt-2">This is the name they'll introduce themselves with on every call.</p>
                <input data-testid="pa-name-input" value={form.assistant_name} onChange={(e) => set("assistant_name", e.target.value)}
                  placeholder="e.g. Ava" className="pa-input w-full mt-6 px-4 py-3 text-lg" />
                <div className="flex flex-wrap gap-2 mt-4">
                  {meta.suggested_names.map((n) => (
                    <button key={n} data-testid={`pa-name-chip-${n}`} onClick={() => set("assistant_name", n)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all pa-hoverable`}
                      style={{ border: "1px solid var(--pa-glass-border)", color: form.assistant_name === n ? "#0A0A0A" : "var(--pa-fg)", background: form.assistant_name === n ? "var(--pa-gold)" : "transparent" }}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="pa-display text-3xl">Voice & personality</h2>
                  <p className="pa-muted text-sm mt-2">Choose how {form.assistant_name || "your assistant"} sounds and behaves.</p>
                </div>
                <Field label="Voice">
                  <select data-testid="pa-voice-select" value={form.voice} onChange={(e) => set("voice", e.target.value)} className="pa-input w-full px-4 py-3">
                    {meta.voices.map((v) => <option key={v} value={v} style={{ color: "#0A0A0A" }}>{v}</option>)}
                  </select>
                </Field>
                <Field label="Accent / language">
                  <select data-testid="pa-accent-select" value={form.accent} onChange={(e) => set("accent", e.target.value)} className="pa-input w-full px-4 py-3">
                    {meta.accents.map((a) => <option key={a} value={a} style={{ color: "#0A0A0A" }}>{a}</option>)}
                  </select>
                </Field>
                <Field label="Personality">
                  <div className="flex flex-wrap gap-2">
                    {meta.personalities.map((p) => (
                      <button key={p} data-testid={`pa-personality-${p}`} onClick={() => set("personality", p)}
                        className="px-4 py-2 rounded-full text-sm pa-hoverable"
                        style={{ border: "1px solid var(--pa-glass-border)", color: form.personality === p ? "#0A0A0A" : "var(--pa-fg)", background: form.personality === p ? "var(--pa-gold)" : "transparent" }}>
                        {p}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="pa-display text-3xl">Meet {form.assistant_name || "Ava"}</h2>
                <p className="pa-muted text-sm mt-2">Here's how they'll greet your callers.</p>
                <div className="pa-plate p-6 mt-6 flex items-center gap-4">
                  <div className="pa-wave flex items-end h-6" aria-hidden>
                    {Array.from({ length: 7 }).map((_, i) => <span key={i} style={{ animationDelay: `${i * 0.1}s` }} />)}
                  </div>
                  <p className="pa-display text-xl leading-snug">
                    "Hello, you've reached {ownerName}'s personal assistant, <span className="pa-gold">{form.assistant_name || "Ava"}</span>. How can I help today?"
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-6 text-sm">
                  <Summary k="Voice" v={form.voice} />
                  <Summary k="Accent" v={form.accent} />
                  <Summary k="Personality" v={form.personality} />
                  <Summary k="Plan" v={(meta.plans.find((p) => p.id === form.plan) || {}).name || form.plan} />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-8">
              <button data-testid="pa-back-btn" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                className="text-sm pa-muted disabled:opacity-30">Back</button>
              {step < 2 ? (
                <button data-testid="pa-next-btn" onClick={() => setStep((s) => s + 1)} className="pa-btn-gold px-6 py-2.5 font-semibold flex items-center gap-2">
                  Continue <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                </button>
              ) : (
                <button data-testid="pa-finish-btn" onClick={submit} disabled={busy} className="pa-btn-gold px-6 py-2.5 font-semibold flex items-center gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4" strokeWidth={1.75} /> Bring {form.assistant_name || "Ava"} to life</>}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <p className="text-xs tracking-[0.2em] uppercase pa-muted font-bold mb-2">{label}</p>
    {children}
  </div>
);
const Summary = ({ k, v }) => (
  <div className="pa-plate px-4 py-3">
    <p className="text-[10px] tracking-widest uppercase pa-muted">{k}</p>
    <p className="mt-0.5 truncate">{v}</p>
  </div>
);
